import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../utils/api';
import { getMyProjects } from '../utils/api';
import { IconTrash } from '../components/Icons';

const Quotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(0);

  const [projectId, setProjectId] = useState(() => {
    const saved = localStorage.getItem('selectedProjectId');
    return saved ? parseInt(saved) : null;
  });

  // Sync projectId changes to localStorage
  useEffect(() => {
    if (projectId) {
      localStorage.setItem('selectedProjectId', projectId);
    }
  }, [projectId]);

  const [status, setStatus] = useState('');
  const [quoteType, setQuoteType] = useState('');
  const [sort, setSort] = useState('date_desc');
  
  // Photo state for edit
  const [editPhotos, setEditPhotos] = useState([]);
  const [editPhotoFiles, setEditPhotoFiles] = useState([]);
  const [editGrayscale, setEditGrayscale] = useState(false);
  const [editCompression, setEditCompression] = useState(0.8);
  
  // Photo state for new quote  
  const [newQuotePhotos, setNewQuotePhotos] = useState([]);
  const [newQuotePhotoFiles, setNewQuotePhotoFiles] = useState([]);
  const [newQuoteGrayscale, setNewQuoteGrayscale] = useState(false);
  const [newQuoteCompression, setNewQuoteCompression] = useState(0.8);
  
  const [error, setError] = useState('');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (projectId) fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, page, status, quoteType, sort]);
  
  const fetchProjects = async () => {
    try {
      const data = await getMyProjects();
      // Only auto-select first project if none selected. The full list is
      // not needed in this page (the project selector lives in the header),
      // so we discard it after the auto-select decision.
      if (data && data.length > 0 && !projectId) {
        setProjectId(data[0].id);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  
  const fetchQuotes = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        project_id: projectId,
        page,
        limit,
        sort
      });
      if (status) params.append('status', status);
      if (quoteType) params.append('quote_type', quoteType);
      
      const data = await api.getQuotes(projectId, status, page, limit, sort, quoteType);
      setQuotes(data.data || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAccept = async (uuid) => {
    if (!window.confirm('Accept this quote?')) return;
    try {
      await api.acceptQuote(uuid);
      fetchQuotes();
    } catch (err) {
      alert(err.message);
    }
  };
  
  const handleReject = async (uuid) => {
    if (!window.confirm('Reject this quote?')) return;
    try {
      await api.rejectQuote(uuid);
      fetchQuotes();
    } catch (err) {
      alert(err.message);
    }
  };
  
  const handleDelete = async (uuid) => {
    if (!window.confirm('Delete this quote permanently?')) return;
    try {
      await api.deleteQuote(uuid);
      fetchQuotes();
    } catch (err) {
      alert(err.message);
    }
  };

  // Photo handlers for edit
  const handleEditFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setEditPhotos([...editPhotos, reader.result]);
        setEditPhotoFiles([...editPhotoFiles, file]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveEditPhoto = (idx) => {
    setEditPhotos(editPhotos.filter((_, i) => i !== idx));
    setEditPhotoFiles(editPhotoFiles.filter((_, i) => i !== idx));
  };

  // Photo handlers for new quote
  const handleNewFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPreviews = files.map(f => URL.createObjectURL(f));
    setNewQuotePhotos([...newQuotePhotos, ...newPreviews]);
    setNewQuotePhotoFiles([...newQuotePhotoFiles, ...files]);
  };

  const handleRemoveNewPhoto = (idx) => {
    setNewQuotePhotos(newQuotePhotos.filter((_, i) => i !== idx));
    setNewQuotePhotoFiles(newQuotePhotoFiles.filter((_, i) => i !== idx));
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // New quote form state
  const [newQuoteForm, setNewQuoteForm] = useState({
    quote_number: '',
    quote_type: '',
    client: '',
    provider: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });
  const [newQuoteSubmitting, setNewQuoteSubmitting] = useState(false);

  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setEditForm(quote);
    setShowQuoteModal(true);
    setIsEditing(false);
  };

  const handleEditQuote = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    try {
      // Ensure date is in proper format
      const formattedForm = {
        ...editForm,
        date: editForm.date ? (editForm.date.includes('T') ? editForm.date.split('T')[0] : editForm.date) : '',
        amount: Math.round((editForm.amount || 0) * 100) / 100
      };
      await api.updateQuote(selectedQuote.uuid, formattedForm);
      setIsEditing(false);
      setSelectedQuote(editForm);
      fetchQuotes();
      alert('Quote updated!');
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCancelEdit = () => {
    setEditForm(selectedQuote);
    setIsEditing(false);
  };

  // Handle create new quote
  const handleCreateQuote = async (e) => {
    e.preventDefault();
    if (!projectId) { alert('Please select a project first'); return; }
    setNewQuoteSubmitting(true);
    try {
      await api.createQuote({ ...newQuoteForm, amount: Math.round(parseFloat(newQuoteForm.amount) * 100) / 100 }, projectId);
      setShowQuoteModal(false);
      setNewQuoteForm({ quote_number: '', quote_type: '', client: '', provider: '', amount: '', date: new Date().toISOString().split('T')[0], description: '' });
      fetchQuotes();
      alert('Quote created!');
    } catch (err) { alert(err.message); }
    finally { setNewQuoteSubmitting(false); }
  };
  
  const getStatusBadge = (status) => {
    const colors = {
      pending: '#ffc107',
      accepted: '#28a745',
      rejected: '#dc3545'
    };
    return (
      <span style={{
        backgroundColor: colors[status] || '#6c757d',
        color: '#fff',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px'
      }}>
        {status?.toUpperCase()}
      </span>
    );
  };
  
  const quoteTypes = ['service', 'product', 'consulting', 'maintenance', 'other'];
  
  return (
    <div className="quotes-page">
      <div className="dashboard-header">
        <h1>Quotes</h1>
        <button className="btn btn-success" onClick={() => setShowQuoteModal(true)}>+ New Quote</button>
      </div>
      
      <div className="filter-bar" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
        
        <select value={quoteType} onChange={(e) => { setQuoteType(e.target.value); setPage(1); }}>
          <option value="">All Types</option>
          {quoteTypes.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
        
        <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
          <option value="date_desc">Newest First</option>
          <option value="date_asc">Oldest First</option>
          <option value="amount_desc">Highest Amount</option>
          <option value="amount_asc">Lowest Amount</option>
          <option value="client_asc">Client A-Z</option>
          <option value="type_asc">Type A-Z</option>
        </select>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Type</th>
                <th>Client</th>
                <th>Provider</th>
                <th>Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {quotes.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center' }}>No quotes found</td></tr>
              ) : (
                quotes.map(q => (
                  <tr key={q.uuid}>
                    <td>{q.quote_number}</td>
                    <td>{q.quote_type || '-'}</td>
                    <td>{q.client}</td>
                    <td>{q.provider || '-'}</td>
                    <td>{formatAmount(q.amount)}</td>
                    <td>{formatDate(q.date)}</td>
                    <td>{getStatusBadge(q.status)}</td>
                    <td>
                      <button className="btn btn-sm" onClick={() => handleViewQuote(q)}>👁</button>
                      {q.status === 'pending' && (
                        <>
                          <button className="btn btn-sm btn-success" onClick={() => handleAccept(q.uuid)} style={{ marginLeft: '4px' }}>✓</button>
                          <button className="btn btn-sm btn-danger" onClick={() => handleReject(q.uuid)} style={{ marginLeft: '4px' }}>✗</button>
                        </>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(q.uuid)} style={{ marginLeft: '4px' }}><IconTrash size={12} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {/* Quote Detail Modal */}
          {showQuoteModal && selectedQuote && (
            <div className="modal-overlay" onClick={() => setShowQuoteModal(false)}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header"><h2>{isEditing ? 'Edit Quote' : 'Quote Details'}</h2><button className="modal-close" onClick={() => setShowQuoteModal(false)}>×</button></div>
                <div style={{ padding: '1rem' }}>
                  {isEditing ? (
                    <>
                      <div className="form-group"><label>Quote #:</label><input type="text" value={editForm.quote_number || ''} onChange={e => setEditForm({...editForm, quote_number: e.target.value})} /></div>
                      <div className="form-group"><label>Type:</label><select value={editForm.quote_type || ''} onChange={e => setEditForm({...editForm, quote_type: e.target.value})}><option value="">Select...</option><option value="service">Service</option><option value="product">Product</option><option value="consulting">Consulting</option><option value="maintenance">Maintenance</option><option value="other">Other</option></select></div>
                      <div className="form-group"><label>Client:</label><input type="text" value={editForm.client || ''} onChange={e => setEditForm({...editForm, client: e.target.value})} /></div>
                      <div className="form-group"><label>Provider:</label><input type="text" value={editForm.provider || ''} onChange={e => setEditForm({...editForm, provider: e.target.value})} /></div>
                      <div className="form-group"><label>Amount:</label><input type="number" step="0.01" value={editForm.amount || ''} onChange={e => setEditForm({...editForm, amount: parseFloat(e.target.value)})} /></div>
                      <div className="form-group"><label>Date:</label><input type="date" value={editForm.date ? editForm.date.split('T')[0] : ''} onChange={e => setEditForm({...editForm, date: e.target.value})} /></div>
                      <div className="form-group"><label>Description:</label><textarea value={editForm.description || ''} onChange={e => setEditForm({...editForm, description: e.target.value})} rows="3" /></div>

                      {/* Photo upload */}
                      <div className="form-group">
                        <label>Evidence Photos</label>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                          <label className="btn btn-sm btn-secondary">📁 Upload
                            <input type="file" accept="image/*" onChange={handleEditFileSelect} style={{ display: 'none' }} />
                          </label>
                          <label className="btn btn-sm btn-secondary">📷 Camera
                            <input type="file" accept="image/*" capture="environment" onChange={handleEditFileSelect} style={{ display: 'none' }} />
                          </label>
                        </div>
                        {editPhotos.length > 0 && (
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {editPhotos.map((src, idx) => (
                              <div key={idx} style={{ position: 'relative' }}>
                                <img src={src} alt="Preview" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', filter: editGrayscale ? 'grayscale(100%)' : 'none' }} />
                                <button type="button" onClick={() => handleRemoveEditPhoto(idx)} style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', width: '18px', height: '18px', fontSize: '12px' }}>×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        {editPhotos.length > 0 && (
                          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              <input type="checkbox" checked={editGrayscale} onChange={(e) => setEditGrayscale(e.target.checked)} />Grayscale
                            </label>
                            <label>Quality: {Math.round(editCompression * 100)}%
                              <input type="range" min="0.1" max="1" step="0.1" value={editCompression} onChange={(e) => setEditCompression(parseFloat(e.target.value))} />
                            </label>
                          </div>
                        )}
                      </div>

                      <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-primary" onClick={handleSaveEdit}>Save</button>
                        <button className="btn btn-outline" onClick={handleCancelEdit}>Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p><strong>Quote #:</strong> {selectedQuote.quote_number}</p>
                      <p><strong>Type:</strong> {selectedQuote.quote_type || '-'}</p>
                      <p><strong>Client:</strong> {selectedQuote.client}</p>
                      <p><strong>Provider:</strong> {selectedQuote.provider || '-'}</p>
                      <p><strong>Amount:</strong> {formatAmount(selectedQuote.amount)}</p>
                      <p><strong>Date:</strong> {formatDate(selectedQuote.date)}</p>
                      <p><strong>Status:</strong> {getStatusBadge(selectedQuote.status)}</p>
                      <p><strong>Description:</strong><br/>{selectedQuote.description || '-'}</p>
                      {selectedQuote.status === 'pending' && (
                        <button className="btn btn-outline" onClick={handleEditQuote} style={{ marginTop: '1rem' }}>Edit</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* New Quote Form */}
          {showQuoteModal && !selectedQuote && (
            <>
              <div className="modal-header"><h2>New Quote</h2><button className="modal-close" onClick={() => setShowQuoteModal(false)}>×</button></div>
              <div className="modal-body">
              <form onSubmit={handleCreateQuote}>
                <div className="form-group"><label>Quote Number</label><input type="text" value={newQuoteForm.quote_number} onChange={(e) => setNewQuoteForm({...newQuoteForm, quote_number: e.target.value})} placeholder="Auto-generated if empty" /></div>
                <div className="form-group"><label>Type</label><select value={newQuoteForm.quote_type || ''} onChange={(e) => setNewQuoteForm({...newQuoteForm, quote_type: e.target.value})}><option value="">Select...</option><option value="service">Service</option><option value="product">Product</option><option value="consulting">Consulting</option><option value="maintenance">Maintenance</option><option value="other">Other</option></select></div>
                <div className="form-group"><label>Client</label><input type="text" value={newQuoteForm.client} onChange={(e) => setNewQuoteForm({...newQuoteForm, client: e.target.value})} placeholder="Who pays us" required /></div>
                <div className="form-group"><label>Provider</label><input type="text" value={newQuoteForm.provider} onChange={(e) => setNewQuoteForm({...newQuoteForm, provider: e.target.value})} placeholder="Who provides service" required /></div>
                <div className="form-group"><label>Amount</label><input type="number" step="0.01" value={newQuoteForm.amount} onChange={(e) => setNewQuoteForm({...newQuoteForm, amount: e.target.value})} required /></div>
                <div className="form-group"><label>Date</label><input type="date" value={newQuoteForm.date} onChange={(e) => setNewQuoteForm({...newQuoteForm, date: e.target.value})} required /></div>
                <div className="form-group"><label>Description</label><textarea value={newQuoteForm.description} onChange={(e) => setNewQuoteForm({...newQuoteForm, description: e.target.value})} rows="3" /></div>

                {/* Photo upload */}
                <div className="form-group">
                  <label>Evidence Photos</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    <label className="btn btn-sm btn-secondary">📁 Upload
                      <input type="file" accept="image/*" multiple onChange={handleNewFileSelect} style={{ display: 'none' }} />
                    </label>
                    <label className="btn btn-sm btn-secondary">📷 Camera
                      <input type="file" accept="image/*" capture="environment" onChange={handleNewFileSelect} style={{ display: 'none' }} />
                    </label>
                  </div>
                  {newQuotePhotos.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {newQuotePhotos.map((src, idx) => (
                        <div key={idx} style={{ position: 'relative' }}>
                          <img src={src} alt="Preview" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', filter: newQuoteGrayscale ? 'grayscale(100%)' : 'none' }} />
                          <button type="button" onClick={() => handleRemoveNewPhoto(idx)} style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', width: '18px', height: '18px', fontSize: '12px' }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {newQuotePhotos.length > 0 && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <input type="checkbox" checked={newQuoteGrayscale} onChange={(e) => setNewQuoteGrayscale(e.target.checked)} />Grayscale
                      </label>
                      <label>Quality: {Math.round(newQuoteCompression * 100)}%
                        <input type="range" min="0.1" max="1" step="0.1" value={newQuoteCompression} onChange={(e) => setNewQuoteCompression(parseFloat(e.target.value))} />
                      </label>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowQuoteModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-success" disabled={newQuoteSubmitting}>{newQuoteSubmitting ? 'Creating...' : 'Create Quote'}</button>
                </div>
              </form>
            </div>
            </>
          )}

          {totalPages > 1 && (
            <div className="pagination" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button className="btn btn-outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <span>Page {page} of {totalPages} ({total} total)</span>
              <button className="btn btn-outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Quotes;
